import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Ticker from "../components/Ticker";
import Footer from "../components/Footer";
import { ArchiveAPI } from "../lib/api";
import { Archive as ArchiveIcon, Loader2, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const ArchivePage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    ArchiveAPI.list({ limit: 500 })
      .then((d) => {
        if (!alive) return;
        setItems(d.items || []);
        setTotal(d.total || 0);
        setLoading(false);
      })
      .catch(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (a) =>
        (a.title || "").toLowerCase().includes(needle) ||
        (a.category || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  // Group by year-month
  const groups = useMemo(() => {
    const map = new Map();
    for (const a of filtered) {
      const d = a.publishedAt ? new Date(a.publishedAt) : null;
      const key = d
        ? d.toLocaleDateString("en-US", { year: "numeric", month: "long" })
        : "Undated";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div data-testid="archive-page">
      <Ticker />
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="pb-6 border-b-2 border-[#0B1F33] mb-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold flex items-center gap-2">
            <ArchiveIcon className="w-3.5 h-3.5" /> The Archive
          </div>
          <h1 className="mt-2 font-serif-display text-[48px] md:text-[60px] font-bold text-[#0B1F33] leading-none">
            The record, preserved.
          </h1>
          <p className="mt-4 font-serif-display italic text-[18px] text-neutral-700 max-w-3xl leading-relaxed">
            Articles are removed from the live edition after twenty-one days and preserved in the Vexent Wire archive.
            This is the public index of everything the archive has swept in.
          </p>
          <div className="mt-4 text-[12px] uppercase tracking-wider text-neutral-500 font-mono-tick" data-testid="archive-count">
            {loading ? "Loading…" : `${total} article${total === 1 ? "" : "s"} preserved`}
          </div>
        </div>

        {/* Search */}
        <div className="mb-10 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by title or section…"
            data-testid="archive-search-input"
            className="rounded-none pl-9 border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1]"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 py-16 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading archive…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-neutral-300 py-16 text-center text-neutral-500">
            {items.length === 0
              ? "The archive is empty. Articles will appear here as they age past 21 days."
              : "No matching articles."}
          </div>
        ) : (
          <div className="space-y-14" data-testid="archive-groups">
            {groups.map(([label, rows]) => (
              <section key={label}>
                <div className="mb-5 pb-2 border-b border-neutral-300 flex items-baseline justify-between">
                  <h2 className="font-serif-display text-[26px] font-bold text-[#0B1F33]">{label}</h2>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono-tick">
                    {rows.length} entr{rows.length === 1 ? "y" : "ies"}
                  </span>
                </div>
                <ul className="divide-y divide-neutral-200">
                  {rows.map((a) => (
                    <li key={a.id || a.slug} className="py-4 grid grid-cols-[92px_1fr_120px] gap-4 items-baseline" data-testid={`archive-row-${a.slug}`}>
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-mono-tick">
                        {fmtDate(a.publishedAt)}
                      </span>
                      <div>
                        <div className="font-serif-display text-[18px] leading-snug font-semibold text-[#0B1F33]">
                          {a.title}
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#0891B2] font-semibold">
                          {a.category || "Editorial"}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 text-right">
                        Preserved
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <div className="mt-16 pt-8 border-t border-neutral-200 text-[13px] text-neutral-600 leading-relaxed">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#0891B2] font-semibold">A note on the archive</span>
          <p className="mt-2 max-w-3xl">
            Full text of archived articles is preserved in the Vexent Wire archive. Public access to the live edition is
            deliberately limited to the most recent twenty-one days — a discipline meant to keep the front page a working
            desk, not a museum. If you need to reference an archived piece, contact the editor.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArchivePage;
